/**
 * Owner: Masa Bayu
 * File: advanced-sync.ts
 * Date:Time: 2026-09-16 18:50
 * Description: T8-E3 SyncQueue — continuity sync; conflicts non-silent.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { assertT8E3SecurityIntact } from './advanced-security';
import { createQxreadId } from './id';
import { assertOptionalTrimmed, assertTimestamp, assertValidId } from './guards';
export const QXREAD_ADVANCED_SYNC_STATES = [
    'queued',
    'syncing',
    'synced',
    'conflict',
    'failed',
];
function assertSyncState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_ADVANCED_SYNC_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_ADVANCED_SYNC_STATES.join(', ')}`);
    }
    return value;
}
export function enqueueAdvancedSyncItem(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead SyncQueue enqueue');
    assertT8E3SecurityIntact();
    assertValidId(input.accountId, 'QXRead sync accountId');
    assertValidId(input.publicationId, 'QXRead sync publicationId');
    const queuedAt = input.queuedAt ?? new Date().toISOString();
    assertTimestamp(queuedAt, 'QXRead sync queuedAt');
    const syncItemId = input.syncItemId !== undefined ? input.syncItemId : createQxreadId('qxrdsync');
    assertValidId(syncItemId, 'QXRead syncItemId');
    return Object.freeze({
        syncItemId,
        accountId: input.accountId,
        publicationId: input.publicationId,
        state: assertSyncState('queued', 'QXRead sync state'),
        conflictNote: null,
        updatedAt: queuedAt,
        inventsEntitlement: false,
        mutatesQxp: false,
        executesFlip: false,
        conflictSilent: false,
    });
}
export function markAdvancedSyncConflict(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead SyncQueue conflict');
    assertT8E3SecurityIntact();
    const note = assertOptionalTrimmed(input.conflictNote, 'QXRead sync conflictNote');
    if (note === null) {
        throw new Error('Sync conflict requires non-empty conflictNote (non-silent)');
    }
    const conflictedAt = input.conflictedAt ?? new Date().toISOString();
    assertTimestamp(conflictedAt, 'QXRead sync conflictedAt');
    return Object.freeze({
        ...input.item,
        state: assertSyncState('conflict', 'QXRead sync state'),
        conflictNote: note,
        updatedAt: conflictedAt,
        inventsEntitlement: false,
        mutatesQxp: false,
        executesFlip: false,
        conflictSilent: false,
    });
}
export function resolveAdvancedSyncConflict(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead SyncQueue resolve');
    assertT8E3SecurityIntact();
    if (input.item.state !== 'conflict') {
        throw new Error('Sync resolve requires conflict state');
    }
    const resolvedAt = input.resolvedAt ?? new Date().toISOString();
    assertTimestamp(resolvedAt, 'QXRead sync resolvedAt');
    return Object.freeze({
        ...input.item,
        state: assertSyncState('synced', 'QXRead sync state'),
        conflictNote: input.item.conflictNote,
        updatedAt: resolvedAt,
        inventsEntitlement: false,
        mutatesQxp: false,
        executesFlip: false,
        conflictSilent: false,
    });
}
//# sourceMappingURL=advanced-sync.js.map